import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ApiError, api, type User } from "./api";

/**
 * Who is signed in, held in one place.
 *
 * The client never decides anything with this. `auth.active_user` on the
 * server is what actually refuses a request; this only decides which screen
 * to render, so a tampered value gets a nicer-looking 403 and nothing more.
 */

interface AuthState {
  user: User | null;
  /** True until the first /me has answered. Not the same as signed out. */
  loading: boolean;
  signIn: (email: string, password: string) => Promise<User>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.me();
      setUser(data);
    } catch (err) {
      // A 401 here is the ordinary "not signed in yet" case on a cold load,
      // not a failure worth showing anybody.
      if (!(err instanceof ApiError) || err.status !== 401) {
        // Anything else is worth knowing about while developing.
        console.warn("Could not resolve the current session:", err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { data } = await api.login(email, password);
    setUser(data);
    return data;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      // Cleared even if the call failed. A session the server already ended
      // must not keep rendering a signed-in shell.
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, signIn, signOut, refresh }),
    [user, loading, signIn, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth used outside AuthProvider.");
  return context;
}
